import { useMutation, useQuery, useQueryClient, UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { QData } from "../../../types/basic-type-list";
import { TResponse, TResponseWithQData } from "../../../types/common";
import {
  ILeaveRequest, listLeaveRequests, approveLeave, rejectLeave,
  IHolidayEntitlement, IHolidayEntitlementUpsertPayload, listHolidayEntitlements, upsertHolidayEntitlement,
} from "./callbacks";

export const useLeaveList = (
  qData: QData
): UseQueryResult<TResponseWithQData<ILeaveRequest[]>, Error> & { refetchWithQuery: (p: Partial<QData>) => void } => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });
  const queryResult = useQuery<TResponseWithQData<ILeaveRequest[]>, Error>({
    queryKey: [API_ENDPOINTS.LEAVE_LIST, { ...queryParams, total: null }],
    queryFn: async () => (await listLeaveRequests(queryParams)).data,
    select: (res) => ({ ...res, data: res.data.map((x) => ({ key: x.id, ...x })), qData: { ...queryParams, total: res.extra?.total } }),
    initialData: { data: [], success: true, message: "", code: 200, qData: { page: 1, limit: 20 }, extra: { page: 1, limit: 20, total: 0 } },
    staleTime: 20000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
  });
  return { ...queryResult, refetchWithQuery: (p) => setQueryParams(p as QData) };
};

export const useLeaveApprove = ({
  onSuccess, onError,
}: { onSuccess?: (res: TResponse<ILeaveRequest>) => void; onError?: (e: any) => void }): UseMutationResult<TResponse<ILeaveRequest>, unknown, number> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await approveLeave(id)).data,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.LEAVE_LIST] });
      onSuccess?.(res);
    },
    onError,
  });
};

export const useHolidayEntitlementList = (
  qData: QData
): UseQueryResult<TResponseWithQData<IHolidayEntitlement[]>, Error> & { refetchWithQuery: (p: Partial<QData>) => void } => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });
  const queryResult = useQuery<TResponseWithQData<IHolidayEntitlement[]>, Error>({
    queryKey: [API_ENDPOINTS.LEAVE_ENTITLEMENT_LIST, { ...queryParams, total: null }],
    queryFn: async () => (await listHolidayEntitlements(queryParams)).data,
    select: (res) => ({ ...res, data: res.data.map((x) => ({ key: x.id, ...x })), qData: { ...queryParams, total: res.extra?.total } }),
    initialData: { data: [], success: true, message: "", code: 200, qData: { page: 1, limit: 20 }, extra: { page: 1, limit: 20, total: 0 } },
    staleTime: 20000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
  });
  return { ...queryResult, refetchWithQuery: (p) => setQueryParams(p as QData) };
};

export const useHolidayEntitlementSave = ({
  onSuccess, onError,
}: { onSuccess?: (res: TResponse<IHolidayEntitlement>) => void; onError?: (e: any) => void }): UseMutationResult<TResponse<IHolidayEntitlement>, unknown, IHolidayEntitlementUpsertPayload> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => (await upsertHolidayEntitlement(data)).data,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.LEAVE_ENTITLEMENT_LIST] });
      onSuccess?.(res);
    },
    onError,
  });
};

export const useLeaveReject = ({
  onSuccess, onError,
}: { onSuccess?: (res: TResponse<ILeaveRequest>) => void; onError?: (e: any) => void }): UseMutationResult<TResponse<ILeaveRequest>, unknown, { id: number; reason: string }> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }) => (await rejectLeave(id, reason)).data,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.LEAVE_LIST] });
      onSuccess?.(res);
    },
    onError,
  });
};
